import type { FC, PropsWithChildren } from 'react';
import {
    AccordionButton,
    AccordionPanel,
    AccordionItem as ChakraAccordionItem,
} from '@chakra-ui/react';
import { GetIcon } from '../../components/icons';
import { Text } from '../typography';
import type { GetIconProps } from '../../components/icons/types';
import type { RenderAs } from '../typography';
import type { TextProps } from '../typography/text/Text';

const renderIcon = ({
    id,
    isExpanded,
    collapsedIcon,
    expandedIcon,
}: {
    id?: string;
    isExpanded: boolean;
    collapsedIcon?: GetIconProps['name'];
    expandedIcon?: GetIconProps['name'];
}): JSX.Element => {
    if (collapsedIcon && expandedIcon) {
        return (
            <div className="flex h-[22px] w-[22px]  items-center justify-center transition-transform">
                <GetIcon
                    id={`${id ? `${id}-` : ''}icon`}
                    name={isExpanded ? expandedIcon : collapsedIcon}
                    size="sm"
                />
            </div>
        );
    }
    return (
        <div
            className={`flex items-center justify-center transition-transform ${
                !collapsedIcon && !expandedIcon && isExpanded ? '-rotate-180' : ''
            }`}
        >
            <GetIcon id={`${id ? `${id}-` : ''}icon`} name="dropdown-arrow-open" size="md" />
        </div>
    );
};

type DefaultIconProps = {
    collapsedIcon?: undefined;
    expandedIcon?: undefined;
};

type CustomIconProps = {
    collapsedIcon: GetIconProps['name'];
    expandedIcon: GetIconProps['name'];
};

type Props = PropsWithChildren<{
    id?: string;
    title: string | React.ReactNode;
    titleTag?: RenderAs;
    titleStyle?: TextProps;
    borderColor?: string;
}> &
    (DefaultIconProps | CustomIconProps);

export const AccordionItem: FC<Props> = ({
    id,
    title,
    children,
    titleTag,
    borderColor = 'inherit',
    collapsedIcon,
    expandedIcon,
    titleStyle,
}) => (
    <ChakraAccordionItem data-testid={id} borderColor={borderColor}>
        {({ isExpanded }) => (
            <>
                <AccordionButton
                    justifyContent="space-between"
                    alignItems="flex-start"
                    padding={0}
                    gap={4}
                    data-testid={`${id}-toggle-button`}
                    textAlign="left"
                >
                    <Text
                        as={titleTag}
                        id={`${id}-title`}
                        style="bold"
                        color="slt-pepper.1000"
                        {...titleStyle}
                    >
                        {title}
                    </Text>
                    {renderIcon({ id, isExpanded, collapsedIcon, expandedIcon })}
                </AccordionButton>
                <AccordionPanel data-testid={`${id}-content`}>{children}</AccordionPanel>
            </>
        )}
    </ChakraAccordionItem>
);

AccordionItem.defaultProps = {
    id: undefined,
    borderColor: 'inherit',
    titleStyle: undefined,
};
